import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const toggleDropdown = (menu: string) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 cursor-pointer">
            <div className="relative w-12 h-12">
              {/* Mountain Peak Design */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-500 to-primary-700 rounded-lg transform rotate-45 shadow-lg"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative z-10">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 6L8 20H24L16 6Z" fill="white" fillOpacity="0.95"/>
                    <path d="M16 10L11 18H21L16 10Z" fill="white" fillOpacity="0.6"/>
                    <circle cx="16" cy="14" r="1.5" fill="white"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <span className={`font-display font-bold text-lg leading-tight ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
                Alpine Precision Molding
              </span>
              <span className={`text-xs font-medium ${isScrolled ? 'text-primary-600' : 'text-primary-300'}`}>
                European Craftsmanship
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'text-slate-700 hover:text-primary-600 hover:bg-primary-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Home
            </Link>

            <div className="relative group">
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1 whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-slate-700 hover:text-primary-600 hover:bg-primary-50'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <span>Services</span>
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                <Link
                  to="/injection-molding"
                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-drop-line mr-2"></i>Injection Molding
                </Link>
                <Link
                  to="/blow-molding"
                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-wind-line mr-2"></i>Blow Molding
                </Link>
                <Link
                  to="/3d-printing"
                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-printer-line mr-2"></i>3D Printing
                </Link>
                <Link
                  to="/toolmaking"
                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-tools-line mr-2"></i>Advanced Toolmaking
                </Link>
              </div>
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'text-slate-700 hover:text-primary-600 hover:bg-primary-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              About Us
            </Link>

            <Link
              to="/projects"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'text-slate-700 hover:text-primary-600 hover:bg-primary-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Projects
            </Link>

            <Link
              to="/resources"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'text-slate-700 hover:text-primary-600 hover:bg-primary-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Resources
            </Link>

            <Link
              to="/news"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'text-slate-700 hover:text-primary-600 hover:bg-primary-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              News
            </Link>

            <Link
              to="/careers"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'text-slate-700 hover:text-primary-600 hover:bg-primary-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Careers
            </Link>

            <Link
              to="/contact"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'text-slate-700 hover:text-primary-600 hover:bg-primary-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link
              to="/instant-quote"
              className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-500/30 whitespace-nowrap cursor-pointer"
            >
              Get Instant Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg cursor-pointer ${
              isScrolled ? 'text-slate-700' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-4 py-4 space-y-2">
            <Link
              to="/"
              className="block px-4 py-2 text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
            >
              Home
            </Link>

            <div>
              <button
                onClick={() => toggleDropdown('services')}
                className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
              >
                <span>Services</span>
                <i className={`ri-arrow-${openDropdown === 'services' ? 'up' : 'down'}-s-line`}></i>
              </button>
              {openDropdown === 'services' && (
                <div className="ml-4 mt-2 space-y-1">
                  <Link
                    to="/injection-molding"
                    className="block px-4 py-2 text-sm text-slate-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
                  >
                    Injection Molding
                  </Link>
                  <Link
                    to="/blow-molding"
                    className="block px-4 py-2 text-sm text-slate-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
                  >
                    Blow Molding
                  </Link>
                  <Link
                    to="/3d-printing"
                    className="block px-4 py-2 text-sm text-slate-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
                  >
                    3D Printing
                  </Link>
                  <Link
                    to="/toolmaking"
                    className="block px-4 py-2 text-sm text-slate-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
                  >
                    Advanced Toolmaking
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className="block px-4 py-2 text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
            >
              About Us
            </Link>

            <Link
              to="/projects"
              className="block px-4 py-2 text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
            >
              Projects
            </Link>

            <Link
              to="/resources"
              className="block px-4 py-2 text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
            >
              Resources
            </Link>

            <Link
              to="/news"
              className="block px-4 py-2 text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
            >
              News
            </Link>

            <Link
              to="/careers"
              className="block px-4 py-2 text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
            >
              Careers
            </Link>

            <Link
              to="/contact"
              className="block px-4 py-2 text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg cursor-pointer"
            >
              Contact
            </Link>

            <Link
              to="/instant-quote"
              className="block px-4 py-2 text-center bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all cursor-pointer"
            >
              Get Instant Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
